import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { MenuSpec } from './types'

const legToneClass = (t?: string) =>
  t === 'success' ? 'text-success' : t === 'danger' ? 'text-danger' : 'text-foreground'

const ROW =
  'w-full flex items-center gap-2.5 text-left text-body px-2 py-1.5 rounded-md transition-colors text-muted-foreground hover:bg-muted/60 hover:text-foreground cursor-pointer'

export interface WorkbenchContextMenuProps {
  menu: MenuSpec | null
  onClose: () => void
}

/**
 * Reusable right-click menu for the chart canvas. Renders at the cursor, flips
 * back on-screen near edges, closes on outside-click / Escape / scroll / resize.
 * items: [{ label, icon, onClick, danger, disabled, shortcut, submenu }]
 * | { separator: true }.
 */
export function WorkbenchContextMenu({ menu, onClose }: WorkbenchContextMenuProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [openSub, setOpenSub] = useState<number | null>(null) // index of the item whose submenu is open

  // Edge flip: measure after paint and write the clamped position straight onto
  // the element (no setState in a layout effect).
  useLayoutEffect(() => {
    const el = ref.current
    if (!menu || !el) return
    const r = el.getBoundingClientRect()
    let x = menu.x
    let y = menu.y
    if (x + r.width > window.innerWidth - 8) x = Math.max(8, window.innerWidth - r.width - 8)
    if (y + r.height > window.innerHeight - 8) y = Math.max(8, window.innerHeight - r.height - 8)
    el.style.left = `${x}px`
    el.style.top = `${y}px`
  }, [menu])

  useEffect(() => {
    if (!menu) return
    const close = () => onClose()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onDown = (e: PointerEvent) => {
      // Containment check covers the submenu too — it renders inside the root div.
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [menu, onClose])

  if (!menu) return null

  return (
    <div
      ref={ref}
      style={{ left: menu.x, top: menu.y }}
      // z-[1250] (bumped from the prototype's z-50) so the menu beats app dialogs at z-[1101].
      className="fixed z-[1250] min-w-[12rem] rounded-md border border-border bg-popover shadow-popover p-1"
      onContextMenu={(e) => e.preventDefault()}
    >
      {menu.items.map((it, i) =>
        it.separator ? (
          <div key={`sep-${i}`} className="my-1 h-px bg-border" />
        ) : it.submenu ? (
          <div
            key={it.label ?? i}
            className="relative"
            onMouseEnter={() => setOpenSub(i)}
            onMouseLeave={() => setOpenSub(null)}
          >
            <button type="button" className={ROW}>
              {it.icon && <it.icon size={14} strokeWidth={1.75} className="shrink-0" />}
              <span className="flex-1">{it.label}</span>
              <ChevronRight size={13} className="shrink-0 text-muted-foreground/60" />
            </button>
            {openSub === i && (
              <div className="absolute left-full top-0 -mt-1 ml-1 min-w-[9rem] rounded-md border border-border bg-popover shadow-popover p-1">
                {it.submenu.map((sub) => (
                  <button
                    key={sub.label}
                    type="button"
                    onClick={() => {
                      sub.onClick?.()
                      onClose()
                    }}
                    className={ROW}
                  >
                    {sub.glyph && (
                      <span className={cn('w-3 text-center shrink-0', legToneClass(sub.tone))}>{sub.glyph}</span>
                    )}
                    <span className="flex-1">{sub.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <button
            key={it.label ?? i}
            type="button"
            disabled={it.disabled}
            onClick={() => {
              it.onClick?.()
              onClose()
            }}
            className={cn(
              'w-full flex items-center gap-2.5 text-left text-body px-2 py-1.5 rounded-md transition-colors',
              it.disabled
                ? 'text-muted-foreground opacity-50 pointer-events-none'
                : it.danger
                  ? 'text-destructive hover:bg-destructive/10 cursor-pointer'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground cursor-pointer',
            )}
          >
            {it.icon && <it.icon size={14} strokeWidth={1.75} className="shrink-0" />}
            <span className="flex-1">{it.label}</span>
            {it.shortcut && <span className="text-caption text-muted-foreground/60 tabular-nums">{it.shortcut}</span>}
          </button>
        ),
      )}
    </div>
  )
}
